import { Bell, Moon, Sun, Menu, LogOut, FolderOpen, User, Download, Languages } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { SidebarToggle } from './sidebar';
import { useSidebar } from '@/contexts/sidebar-context';
import { useTheme } from '@/contexts/theme-context';
import { useI18n } from '@/contexts/i18n-hook';
import { useAuth } from '@/contexts/auth-hook';
import { useProjeto } from '@/contexts/projeto-hook';
import { useState, useEffect, useRef } from 'react';
import { AUTH_OFFLINE_ALLOWED_UNTIL_KEY } from '@/lib/session-utils';

function getOfflineDaysRemaining(): number | null {
  const raw = localStorage.getItem(AUTH_OFFLINE_ALLOWED_UNTIL_KEY);
  if (!raw) return null;
  const limit = new Date(raw);
  if (Number.isNaN(limit.getTime())) return null;
  const diff = limit.getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (24 * 60 * 60 * 1000)));
}

export function Header() {
  const { toggleSidebar } = useSidebar();
  const { theme, toggleTheme } = useTheme();
  const { idioma, alternarIdioma, t, formatarDataHora } = useI18n();
  const { user, logout, isOnline, offlineSessionActive, lastSyncAt: authLastSyncAt } = useAuth();
  const { projeto, projetos, carregarProjetos, stale, lastSyncAt: projetoLastSyncAt } = useProjeto();
  const navigate = useNavigate();
  const [mostrarNotificacoes, setMostrarNotificacoes] = useState(false);
  const [pwaInstallPrompt, setPwaInstallPrompt] = useState<Event | null>(null);
  const [offlineDaysRemaining, setOfflineDaysRemaining] = useState<number | null>(null);
  const notificacoesRef = useRef<HTMLDivElement>(null);
  const requestedInitialProjectsRef = useRef(false);

  useEffect(() => {
    if (requestedInitialProjectsRef.current) return;
    const needsInitialLoad = stale || (!projetoLastSyncAt && projetos.length === 0);
    if (navigator.onLine && needsInitialLoad) {
      requestedInitialProjectsRef.current = true;
      carregarProjetos();
    }
  }, [carregarProjetos, stale, projetoLastSyncAt, projetos.length]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificacoesRef.current && !notificacoesRef.current.contains(event.target as Node)) {
        setMostrarNotificacoes(false);
      }
    };

    if (mostrarNotificacoes) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mostrarNotificacoes]);

  // Capturar evento de instalação do PWA
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setPwaInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Atualizar dias restantes da sessão offline
  useEffect(() => {
    setOfflineDaysRemaining(getOfflineDaysRemaining());
  }, [offlineSessionActive, isOnline]);

  const handleInstallPwa = async () => {
    if (!pwaInstallPrompt) return;
    const promptEvent = pwaInstallPrompt as Event & { prompt: () => void; userChoice: Promise<{ outcome: string }> };
    promptEvent.prompt();
    await promptEvent.userChoice;
    setPwaInstallPrompt(null);
  };

  const connectivityLabel = isOnline ? t('layout.header.online') : t('layout.header.offline');
  const connectivityClassName = isOnline
    ? 'text-green-700 border-green-300 bg-green-50'
    : 'text-amber-700 border-amber-300 bg-amber-50';
  const syncTimestamp = projetoLastSyncAt || authLastSyncAt;

  const handleLogout = async () => {
    // logout avisa o servidor para revogar a sessão antes de sair da tela.
    await logout();
    navigate('/login');
  };

  return (
    <>
      <div className="md:block hidden">
        <SidebarToggle />
      </div>
      <header className="bg-white dark:bg-schmersal-gray-darker border-b border-rg-line dark:border-schmersal-gray-dark h-16">
        <div className="flex items-center justify-between px-4 md:px-6 h-full">
          <div className="flex items-center h-full">
            {/* Mobile Toggle */}
            <button
              onClick={toggleSidebar}
              className="md:hidden p-2 -ml-2 mr-2 text-schmersal-gray-dark dark:text-white hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark rounded-lg"
            >
              <Menu className="h-6 w-6" />
            </button>

          </div>

          {/* Projeto Selecionado */}
          {projeto && (
            <div className="flex items-center space-x-2 px-3 py-1.5 bg-rg-head dark:bg-schmersal-gray-dark border border-rg-line dark:border-transparent rounded-lg">
              <FolderOpen className="h-4 w-4 text-schmersal-blue dark:text-white" />
              <span className="text-[13px] font-semibold text-schmersal-blue dark:text-white">
                {projeto.nome}
              </span>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center space-x-3 ml-auto">
            {/* Idioma: um clique alterna PT/EN e a escolha fica salva no
                navegador (ver I18nProvider). */}
            <button
              onClick={alternarIdioma}
              className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-[13px] font-semibold
                       text-schmersal-gray-dark dark:text-white
                       hover:bg-schmersal-gray-light dark:hover:bg-schmersal-blue hover:text-schmersal-blue dark:hover:text-schmersal-yellow
                       transition-colors duration-200"
              title={t('layout.header.trocarIdioma')}
              aria-label={t('layout.header.trocarIdioma')}
            >
              <Languages className="h-5 w-5" />
              <span className="uppercase">{idioma}</span>
            </button>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="relative p-2.5 rounded-lg text-schmersal-gray-dark dark:text-white
                       hover:bg-schmersal-gray-light dark:hover:bg-schmersal-blue hover:text-schmersal-blue dark:hover:text-schmersal-yellow
                       transition-colors duration-200"
              title={theme === 'dark' ? t('layout.header.modoClaro') : t('layout.header.modoEscuro')}
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>

            {/* Notifications */}
            <div className="relative" ref={notificacoesRef}>
              <button
                onClick={() => setMostrarNotificacoes(!mostrarNotificacoes)}
                className="relative p-2.5 rounded-lg text-schmersal-gray-dark dark:text-white
                         hover:bg-schmersal-gray-light dark:hover:bg-schmersal-blue hover:text-schmersal-blue dark:hover:text-schmersal-yellow
                         transition-colors duration-200"
                title={t('layout.header.notificacoes')}
              >
                <Bell className="h-5 w-5" />
                <span className="absolute top-1.5 right-1.5 block h-2.5 w-2.5 
                               bg-schmersal-yellow rounded-full border-2 border-white dark:border-schmersal-gray-darker"></span>
              </button>

              {mostrarNotificacoes && (
                <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-lg border border-schmersal-gray-light dark:border-schmersal-gray-dark z-50">
                  <div className="p-4 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
                    <h3 className="font-semibold text-schmersal-gray-dark dark:text-white">{t('layout.header.notificacoes')}</h3>
                  </div>
                  <div className="p-4 text-center text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
                    {t('layout.header.semNotificacoes')}
                  </div>
                </div>
              )}
            </div>

            {/* Versão do app (build) */}
            <div
              className="flex items-center px-2 py-1 rounded text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light border border-schmersal-gray-light dark:border-schmersal-gray-dark"
              title={t('layout.header.build', {
                data: typeof __BUILD_TIME__ !== 'undefined' ? formatarDataHora(__BUILD_TIME__) : '-',
              })}
            >
              v{typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '1.0.0'}
            </div>

            {/* Status de conectividade */}
            <div
              className={`flex items-center px-2 py-1 rounded text-xs border ${connectivityClassName}`}
              title={
                syncTimestamp
                  ? t('layout.header.ultimaAtualizacao', { data: formatarDataHora(syncTimestamp) })
                  : t('layout.header.semAtualizacao')
              }
            >
              {connectivityLabel}
              {!isOnline && offlineDaysRemaining !== null && (
                <span className="ml-1 opacity-75">
                  ({offlineDaysRemaining}d)
                </span>
              )}
            </div>

            {/* Botão de instalação do PWA */}
            {pwaInstallPrompt && (
              <button
                onClick={handleInstallPwa}
                className="flex items-center space-x-1 px-2 py-1 rounded text-xs border text-schmersal-blue border-schmersal-blue bg-schmersal-blue-light dark:bg-schmersal-gray-dark dark:text-white dark:border-schmersal-gray-dark hover:bg-schmersal-blue hover:text-white transition-colors"
                title={t('layout.header.instalarApp')}
              >
                <Download className="h-3 w-3" />
                <span className="hidden sm:inline">{t('layout.header.instalar')}</span>
              </button>
            )}

            {/* User Menu */}
            <div className="flex items-center space-x-3 pl-3 border-l-2 border-schmersal-gray-light dark:border-schmersal-gray-dark">
              <Link
                to="/perfil"
                className="flex items-center space-x-3 rounded-lg p-1.5 hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-dark transition-colors"
                title={t('layout.header.meuPerfil')}
              >
                <div className="w-9 h-9 rounded-full overflow-hidden bg-schmersal-blue-light dark:bg-schmersal-gray-dark flex items-center justify-center flex-shrink-0">
                  {user?.fotoUrl ? (
                    <img src={user.fotoUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="h-5 w-5 text-schmersal-blue dark:text-white" />
                  )}
                </div>
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-semibold text-schmersal-blue dark:text-white">
                    {user?.nome || t('layout.header.usuario')}
                  </p>
                  <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">
                    {user?.role === 'admin' ? t('layout.header.administrador') : t('layout.header.usuario')}
                  </p>
                </div>
              </Link>
              <button
                onClick={handleLogout}
                className="p-2.5 rounded-lg text-schmersal-gray-dark dark:text-white
                         hover:bg-red-600 hover:text-white
                         transition-colors duration-200"
                title={t('layout.header.sair')}
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

    </>
  );
}
